import { useRef, useEffect } from 'react';
import { useRouter } from 'next/router';
import { segment } from '@actions/analytics';
import { OFFLINE_CENTRE_PAGE_LANDED } from '@cont/OfflineCentres/constants';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import { getIsLoggedIn, getMe } from '@api/selectors/auth';
import useOfflineCentreStore from '@stores/city-specific-offline-centres';

import {
  fetchReverseGeocodeData,
  fetchUserLocationData,
  postUserLocationData
} from '@api/fetchers/geocode';
import { captureException } from '@utils/sentry';
import { followGoal, fetchPinnedGoals } from '@api/fetchers/goal/updaters';
import {
  centreDataProcessor,
  onScrollHandler,
  distance,
  CITY_RADIUS_IN_KM,
  REVERSE_GEOCODE_ERROR
} from './utils';
import BookAVisitDrawer from './BookAVisitDrawer';
import {
  NOT_FOUND,
  FOUND,
  OFFLINE_LOCATION_PERMISSION_SELECTED
} from './constants';
import VisitBookedForCenter from './VisitBookedForCenter';

const OfflineCentres = ({ data, source }) => {
  const { query } = useRouter();
  const { goalUID } = query;
  const {
    setUserLocation,
    setIsBatchesOnTop,
    setIsBatchesVisibleZus,
    setCentreData,
    selectedCentreBEData,
    selectedCentreFound,
    setIsBookAVisitEnabledFromCMS,
    isBookAVisitFlow,
    isVisitBooked
  } = useOfflineCentreStore();
  useEffect(() => {
    setIsBookAVisitEnabledFromCMS(data.isBookAVisitFlowEnabled);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const batchesRef = useRef();
  const batchesRefFilterRef = useRef();
  const batchesTopRef = useRef(false);
  const { cityName } = data || {};
  const meData = useMyInfo(getMe);
  const isLoggedIn = useMyInfo(getIsLoggedIn);
  const { userID } = meData;
  const { centreName: cmsCentreName, cityName: cmsCityName } = data || {};
  const { city, state, name: centreName } = selectedCentreBEData || {};

  useEffect(() => {
    setCentreData(data);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  useEffect(() => {
    if (isLoggedIn && goalUID !== 'JVITK') {
      fetchPinnedGoals().then((followedGoals) => {
        const shouldPinGoal = !followedGoals?.find(
          ({ uid: followedGoalUID }) => followedGoalUID === goalUID
        );
        if (shouldPinGoal) {
          followGoal(goalUID, true);
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn]);

  const successCallback = async (postion) => {
    const { coords } = postion;
    const { latitude, longitude } = coords;
    setUserLocation({ lat: latitude, long: longitude });
    let previousLocation = null;
    if (isLoggedIn) {
      previousLocation = await fetchUserLocationData();
    }

    const currentPosition = { lat: latitude, long: longitude };
    const previousPosition = {
      lat: previousLocation?.lat,
      long: previousLocation?.long
    };

    const isLocationChanged =
      distance(previousPosition, currentPosition) > CITY_RADIUS_IN_KM ||
      !previousLocation;

    if (isLoggedIn && isLocationChanged) {
      let address = {};
      await fetchReverseGeocodeData(latitude, longitude).then((res) => {
        if (!res?.address) {
          const extraTags = [
            {
              type: 'tag',
              key: 'latitude',
              value: latitude
            },
            {
              type: 'tag',
              key: 'longitude',
              value: longitude
            },
            {
              type: 'tag',
              key: 'userID',
              value: userID
            }
          ];
          captureException(
            new Error(REVERSE_GEOCODE_ERROR),
            null,
            null,
            extraTags
          );
        } else address = res?.address;
      });

      const body = {
        lat: latitude,
        long: longitude,
        user: userID,
        city: address?.city,
        state: address?.state,
        country: address?.country
      };

      postUserLocationData(body);
      segment.track(OFFLINE_LOCATION_PERMISSION_SELECTED, {
        centre_name: centreName || cmsCentreName,
        city: city || cmsCityName,
        state,
        goal_uid: goalUID,
        latitude,
        longitude,
        learner_location: {
          latitude,
          longitude
        },
        city_predicted: address?.city || 'NA',
        state_predicted: address?.state || 'NA',
        location_permission_type: 'allowed'
      });
    }
  };
  const errorCallback = () => {
    if (isLoggedIn) {
      segment.track(OFFLINE_LOCATION_PERMISSION_SELECTED, {
        centre_name: centreName,
        city: cityName,
        goal_uid: goalUID,
        latitude: 'NA',
        longitude: 'NA',
        learner_location: {
          latitude: 'NA',
          longitude: 'NA'
        },
        city_predicted: 'NA',
        state_predicted: 'NA',
        location_permission_type: 'declined'
      });
    }
  };

  const isUserDataLoaded = useMyInfo(getIsLoggedIn);
  const { isLoading: isUserDataLoading, isValidating: userDataNeedsLoading } =
    useMyInfo();

  const isUserLoggedIn = !isUserDataLoading && isUserDataLoaded;
  const isUserLoggedOut = !userDataNeedsLoading && !isUserDataLoaded;

  useEffect(() => {
    if (isUserLoggedIn || isUserLoggedOut) {
      navigator.geolocation.getCurrentPosition(successCallback, errorCallback);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUserLoggedIn, isUserLoggedOut]);
  useEffect(() => {
    if (
      (isUserLoggedIn || isUserLoggedOut) &&
      (selectedCentreFound === NOT_FOUND || selectedCentreFound === FOUND)
    ) {
      if (
        (selectedCentreFound === FOUND && state) ||
        selectedCentreFound === NOT_FOUND
      )
        segment.track(OFFLINE_CENTRE_PAGE_LANDED, {
          centre_name: centreName || cmsCentreName,
          goal_uid: goalUID,
          page_landed_source: source,
          city: city || cmsCityName,
          state
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUserLoggedOut, isUserLoggedIn, selectedCentreFound, state]);

  useEffect(() => {
    if (batchesRef.current) {
      const intersectionObserver = new IntersectionObserver(
        (entries) => {
          const batchDiv = entries[0];
          setIsBatchesVisibleZus(batchDiv.isIntersecting);
        },
        { threshold: 0.08 }
      );
      intersectionObserver.observe(batchesRef.current);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batchesRef.current]);

  useEffect(() => {
    window.addEventListener(
      'scroll',
      onScrollHandler(
        batchesRef,
        batchesTopRef,
        setIsBatchesOnTop,
        batchesRefFilterRef
      )
    );
    return () => {
      window.removeEventListener('scroll', onScrollHandler);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const blocks = centreDataProcessor(
    data.data,
    batchesRef,
    batchesRefFilterRef
  );

  return (
    <>
      {blocks}
      {isBookAVisitFlow ? <BookAVisitDrawer /> : null}
      {isVisitBooked ? <VisitBookedForCenter /> : null}
    </>
  );
};

export default OfflineCentres;
